import type { VFrameBase } from '@festina/life-general-types/life'
import { type Optional, type Server, typeUtils } from '@festina-life/core'

import {
  isDontUseVFrame,
  isFixedVFrame,
  isRequiredInModelFrame,
} from './yup/validation/types'
import { isVFrameWithDefaultValue } from './yup/validation/yupValidationUtils'

const isRequiredVFrame = (frame: Optional<VFrameBase>) =>
  !!(frame && 'required' in frame && frame.required)

export const isFieldRequired = <T extends VFrameBase>(frame: Optional<T>) =>
  isRequiredInModelFrame(frame) ||
  (isRequiredVFrame(frame) &&
    !isVFrameWithDefaultValue(frame) &&
    !isFixedVFrame(frame))

export const canFieldBeSubmitted = (frame: unknown) =>
  typeUtils.hasField(frame, '_type') &&
  typeof frame._type === 'string' &&
  !isDontUseVFrame(frame as VFrameBase) &&
  (!isFixedVFrame(frame as VFrameBase) ||
    isRequiredInModelFrame(frame as VFrameBase))

/**
 * If the name has .[numeric value]. or .[numeric value] or [numeric value]., the function modifies the numeric value to be 0 (.0. or .0 or 0.).
 *
 * When using FormikFieldArray, field names include an index number (e.g., fieldName.0.example, fieldName.1.example).
 * To validate all entries in the FormikFieldArray, the 0th element of the array inside the frame is used.
 * This is because the array in the frame typically has only one object at the 0th index.
 *
 * @example
 * ```
 * name = 'amendEmployerAgreementCoverDatas.3.templateId'
 *  // amendEmployerAgreementCoverDatas.0.templateId
 *
 * name = 'amendEmployerAgreementCoverDatas.2.templateId.3.subTemplateId'
 *  // 'amendEmployerAgreementCoverDatas.0.templateId.0.subTemplateId'
 *
 * name = 'amendEmployerAgreementCoverDatas.1'
 *  // amendEmployerAgreementCoverDatas.0
 *
 * name = '1.amendEmployerAgreementCoverDatas'
 *  // 0.amendEmployerAgreementCoverDatas
 * ```
 */

export const modifyNameToFirstEntry = (name: string) =>
  name.replace(/\b\d+\b/g, '0')

export const queryKeyForFetchFrame = <T>({
  festinaComponent,
  fetchFrameArg,
  scenarioId,
  frameQueryKeySuffix,
}: {
  festinaComponent: Server | undefined
  fetchFrameArg: T
  scenarioId?: string
  frameQueryKeySuffix?: string
}):
  | [Server | undefined, string, T, string | undefined, string]
  | [Server | undefined, string, T, string | undefined] => {
  const common: [Server | undefined, string, T, string | undefined] = [
    festinaComponent,
    'LifeForm:fetchFrame', // arbitrary but unique
    fetchFrameArg,
    frameQueryKeySuffix, // keeps cached frames separate for endpoints with the same fetchFrame arguments
  ]
  return scenarioId ? [...common, scenarioId] : common
}
